import { useRef, useState } from 'react';

import {
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type TypographyProps,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { writeClipboard } from '@/designs-dev/loft/command';
import { editCommand, useEditing } from '@/designs-dev/explore/editing';

export type EditableTextProps = TypographyProps & {
  // Spec path to the field, e.g. `sections[0].frames[1].chasing`.
  path: string;
  // Human label for the same place, e.g. `Frame A2 — what I'm chasing`.
  // Carried alongside the path because indices go stale the moment frames
  // get reordered, and Claude can find it from the label either way.
  what: string;
  value: string;
};

// Any spec-authored string on the page: click it to open an edit box, write
// the new wording, then copy a command that tells Claude exactly what to
// change and where. The edit is local and throwaway — nothing is written back
// to the spec from the browser, in keeping with Loft being read-only.
const EditableText = ({
  path,
  what,
  value,
  ...typography
}: EditableTextProps) => {
  const { sourceFile, onCopied, enabled } = useEditing();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // The rendered width of the text, captured on the click that opens the
  // editor. Without it the field defaults to the full container and the page
  // visibly jumps — the box should sit exactly where the words were.
  const [width, setWidth] = useState<number>();
  const textRef = useRef<HTMLElement>(null);

  const open = () => {
    setWidth(textRef.current?.offsetWidth);
    setDraft(value);
    setEditing(true);
  };

  const copy = () => {
    writeClipboard(
      editCommand({ sourceFile, path, what, before: value, after: draft }),
    );
    onCopied();
    setEditing(false);
  };

  // View mode: plain text, no hover, no cursor, nothing to click. The whole
  // point of the mode is that the page stops behaving like a tool.
  if (!enabled) {
    return <Typography {...typography}>{value}</Typography>;
  }

  if (!editing) {
    return (
      <Typography
        {...typography}
        ref={textRef}
        onClick={open}
        sx={{
          cursor: 'text',
          borderRadius: `${radius.sm}px`,
          // A quiet affordance: nothing until hover, so seventeen frames'
          // worth of editable text doesn't read as decoration.
          transition: 'background-color 120ms',
          '&:hover': { backgroundColor: 'custom.neutral.100' },
          ...typography.sx,
        }}
      >
        {value}
      </Typography>
    );
  }

  return (
    <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1}>
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false);
          // Meta/Ctrl+Enter copies without reaching for the mouse.
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) copy();
        }}
        multiline
        size='small'
        autoFocus
        sx={{ width: width ?? '100%' }}
      />
      <Tooltip title='Copy the edit as a command for Claude'>
        <span>
          <IconButton
            size='small'
            onClick={copy}
            aria-label='Copy the edit as a command for Claude'
            disabled={draft === value}
          >
            <Icon icon={'copy' as never} fontSize={15} />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton
        size='small'
        onClick={() => setEditing(false)}
        aria-label='Cancel'
      >
        <Icon icon={'xmark' as never} fontSize={15} />
      </IconButton>
    </Stack>
  );
};

EditableText.displayName = 'EditableText';
export default EditableText;
